import { useState, useEffect } from 'react'
import { Bell, Mail, Smartphone, Globe, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { motion } from 'framer-motion'
import { toast } from 'sonner'
import { dashboardApi } from '@/services/api'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

export function DashboardNotifications() {
  const queryClient = useQueryClient()

  // Fetch notification settings from API
  const { data: settingsData, isLoading } = useQuery({
    queryKey: ['notificationSettings'],
    queryFn: async () => {
      const response = await dashboardApi.getNotificationSettings()
      return response.data
    },
    onError: () => {
      toast.error('Failed to load notification settings')
    }
  })

  const [settings, setSettings] = useState({
    // Email Notifications
    emailNewArticles: true,
    emailWeeklyDigest: true,
    emailSubscriptionUpdates: true,
    emailPromotions: false,
    
    // Push Notifications
    pushBreakingNews: true,
    pushNewArticles: false,
    pushComments: true,
    pushSubscription: true,
    
    // Frequency
    frequency: 'instant', // instant, daily, weekly
  })

  // Update settings when API data loads
  useEffect(() => {
    if (settingsData) {
      setSettings(prev => ({
        ...prev,
        ...settingsData
      }))
    }
  }, [settingsData])

  // Mutation for saving settings
  const saveMutation = useMutation({
    mutationFn: (data) => dashboardApi.updateNotificationSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['notificationSettings'])
      toast.success('Notification preferences saved!')
    },
    onError: () => {
      toast.error('Failed to save preferences')
    }
  })

  const handleToggle = (key) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }))
  }

  const handleSave = () => {
    saveMutation.mutate(settings)
  }

  return (
    <div className="w-full">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
          <Bell className="w-8 h-8 text-primary" />
          Notification Settings
        </h1>
        <p className="text-gray-600">Manage how and when you receive notifications</p>
      </div>

      <div className="space-y-6">
        {/* Email Notifications */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border border-gray-200 overflow-hidden"
        >
          <div data-logo-box className="bg-gradient-to-r from-slate-700 to-slate-800 p-6 transition-all duration-300">
            <div className="flex items-center gap-3 text-white">
              <Mail className="w-6 h-6" />
              <div>
                <h2 className="text-xl font-semibold">Email Notifications</h2>
                <p className="text-slate-100 text-sm">Receive updates via email</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Label htmlFor="emailNewArticles" className="text-base font-medium text-gray-900">
                  New Articles
                </Label>
                <p className="text-sm text-gray-600 mt-1">
                  Get notified when new articles are published from your subscribed sources
                </p>
              </div>
              <Switch
                id="emailNewArticles"
                checked={settings.emailNewArticles}
                onCheckedChange={() => handleToggle('emailNewArticles')}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Label htmlFor="emailWeeklyDigest" className="text-base font-medium text-gray-900">
                  Weekly Digest
                </Label>
                <p className="text-sm text-gray-600 mt-1">
                  Receive a weekly summary of top stories and trending articles
                </p>
              </div>
              <Switch
                id="emailWeeklyDigest"
                checked={settings.emailWeeklyDigest}
                onCheckedChange={() => handleToggle('emailWeeklyDigest')}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Label htmlFor="emailSubscriptionUpdates" className="text-base font-medium text-gray-900">
                  Subscription Updates
                </Label>
                <p className="text-sm text-gray-600 mt-1">
                  Important updates about your subscription and billing
                </p>
              </div>
              <Switch
                id="emailSubscriptionUpdates"
                checked={settings.emailSubscriptionUpdates}
                onCheckedChange={() => handleToggle('emailSubscriptionUpdates')}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Label htmlFor="emailPromotions" className="text-base font-medium text-gray-900">
                  Promotions & Offers
                </Label>
                <p className="text-sm text-gray-600 mt-1">
                  Special offers, discounts, and promotional content
                </p>
              </div>
              <Switch
                id="emailPromotions"
                checked={settings.emailPromotions}
                onCheckedChange={() => handleToggle('emailPromotions')}
              />
            </div>
          </div>
        </motion.div>

        {/* Push Notifications */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl border border-gray-200 overflow-hidden"
        >
          <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-6">
            <div className="flex items-center gap-3 text-white">
              <Smartphone className="w-6 h-6" />
              <div>
                <h2 className="text-xl font-semibold">Push Notifications</h2>
                <p className="text-purple-100 text-sm">Instant alerts on your device</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Label htmlFor="pushBreakingNews" className="text-base font-medium text-gray-900">
                  Breaking News
                </Label>
                <p className="text-sm text-gray-600 mt-1">
                  Urgent breaking news and important updates
                </p>
              </div>
              <Switch
                id="pushBreakingNews"
                checked={settings.pushBreakingNews}
                onCheckedChange={() => handleToggle('pushBreakingNews')}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Label htmlFor="pushNewArticles" className="text-base font-medium text-gray-900">
                  New Articles
                </Label>
                <p className="text-sm text-gray-600 mt-1">
                  Push notifications for new articles from your sources
                </p>
              </div>
              <Switch
                id="pushNewArticles"
                checked={settings.pushNewArticles}
                onCheckedChange={() => handleToggle('pushNewArticles')}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Label htmlFor="pushComments" className="text-base font-medium text-gray-900">
                  Comments & Replies
                </Label>
                <p className="text-sm text-gray-600 mt-1">
                  When someone replies to your comments
                </p>
              </div>
              <Switch
                id="pushComments"
                checked={settings.pushComments}
                onCheckedChange={() => handleToggle('pushComments')}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Label htmlFor="pushSubscription" className="text-base font-medium text-gray-900">
                  Subscription Alerts
                </Label>
                <p className="text-sm text-gray-600 mt-1">
                  Billing reminders and subscription status changes
                </p>
              </div>
              <Switch
                id="pushSubscription"
                checked={settings.pushSubscription}
                onCheckedChange={() => handleToggle('pushSubscription')}
              />
            </div>
          </div>
        </motion.div>

        {/* Notification Frequency */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl border border-gray-200 overflow-hidden"
        >
          <div className="bg-gradient-to-r from-green-600 to-green-700 p-6">
            <div className="flex items-center gap-3 text-white">
              <Globe className="w-6 h-6" />
              <div>
                <h2 className="text-xl font-semibold">Notification Frequency</h2>
                <p className="text-green-100 text-sm">Control how often you receive notifications</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { value: 'instant', label: 'Instant', desc: 'Receive notifications immediately' },
                { value: 'daily', label: 'Daily Digest', desc: 'Once per day summary' },
                { value: 'weekly', label: 'Weekly Digest', desc: 'Once per week summary' }
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setSettings(prev => ({ ...prev, frequency: option.value }))}
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    settings.frequency === option.value
                      ? 'border-primary bg-slate-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-gray-900">{option.label}</h3>
                    {settings.frequency === option.value && (
                      <Check className="w-5 h-5 text-primary" />
                    )}
                  </div>
                  <p className="text-sm text-gray-600">{option.desc}</p>
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Save Button */}
        <div className="flex justify-end gap-3">
          <Button variant="outline">Reset to Defaults</Button>
          <Button onClick={handleSave} className="gap-2">
            <Check className="w-4 h-4" />
            Save Preferences
          </Button>
        </div>
      </div>
    </div>
  )
}
